import {
  Container,
  Link,
  SeverityOverview,
  Tag,
  Text,
} from "@fluidattacks/design";
import type { CellContext } from "@tanstack/react-table";
import { isNumber } from "lodash";
import { Fragment, useContext } from "react";
import { useLocation } from "react-router-dom";

import { TreatmentTag } from "./styles";

import type { IFindingAttr } from "../types";
import { meetingModeContext } from "context/meeting-mode";
import { useSettingsStore } from "hooks/use-settings-store";
import { translate } from "utils/translations/translate";

const FindingPriorityFormatter = (
  cell: CellContext<IFindingAttr, unknown>,
  groupTotalPriority: number,
): JSX.Element => {
  const MIN_PRIORITY = 0.001;
  const { totalOpenPriority: priority, status } = cell.row.original;
  const isVulnerable = status === "VULNERABLE";
  const value = priority / (groupTotalPriority || -1);

  const defaultValue = isVulnerable ? "0.1%" : "0.0%";

  return (
    <div>
      {value <= MIN_PRIORITY ? defaultValue : `${(value * 100).toFixed(2)}%`}
    </div>
  );
};

const FindingTitleFormatter = (
  cell: CellContext<IFindingAttr, unknown>,
): JSX.Element => {
  const DAYS_IN_A_WEEK = 7;
  const finding: IFindingAttr = cell.row.original;
  const { pathname } = useLocation();
  const vulnerabilityUrl = `${pathname}/${finding.id}/locations`;
  const { meetingMode } = useContext(meetingModeContext);

  const isNew =
    finding.lastVulnerability <= DAYS_IN_A_WEEK &&
    finding.vulnerabilitiesSummary.open > 0;

  const isReview =
    !meetingMode &&
    ((isNumber(finding.rejectedVulnerabilities) &&
      finding.rejectedVulnerabilities > 0) ||
      (isNumber(finding.submittedVulnerabilities) &&
        finding.submittedVulnerabilities > 0));

  return (
    <Container alignItems={"center"} display={"flex"} gap={0.25}>
      <Link href={vulnerabilityUrl} wrap={true}>
        {finding.title}
      </Link>
      {isNew && (
        <Tag
          priority={"medium"}
          tagLabel={translate.t("formatters.includeTags.new")}
          variant={"error"}
        />
      )}
      {isReview && (
        <Tag
          priority={"medium"}
          tagLabel={translate.t("formatters.includeTags.review")}
          variant={"warning"}
        />
      )}
    </Container>
  );
};

const SeverityOverviewFormatter = (
  cell: CellContext<IFindingAttr, unknown>,
): JSX.Element => {
  const { cvssVersion } = useSettingsStore();
  const severityOverview =
    cvssVersion.summaryKey === "vulnerabilitiesSummaryV4"
      ? cell.row.original.vulnStats.severityOverview
      : cell.row.original[cvssVersion.summaryKey];

  const { defaultCritical, defaultHigh, defaultLow, defaultMedium } = {
    defaultCritical: 0,
    defaultHigh: 0,
    defaultLow: 0,
    defaultMedium: 0,
  };

  if ("critical" in severityOverview && "high" in severityOverview) {
    const { critical, high, low, medium } = severityOverview;

    return (
      <SeverityOverview
        critical={critical}
        high={high}
        low={low}
        medium={medium}
      />
    );
  } else if (
    "openCritical" in severityOverview &&
    "openHigh" in severityOverview
  ) {
    const { openCritical, openHigh, openLow, openMedium } = severityOverview;

    return (
      <SeverityOverview
        critical={openCritical}
        high={openHigh}
        low={openLow}
        medium={openMedium}
      />
    );
  }

  return (
    <SeverityOverview
      critical={defaultCritical}
      high={defaultHigh}
      low={defaultLow}
      medium={defaultMedium}
    />
  );
};

const FindingTreatmentFormatter = (
  cell: CellContext<IFindingAttr, unknown>,
): JSX.Element => {
  const treatment = cell.row.original.treatmentSummary;

  const isThereTreatments =
    treatment.untreated +
      treatment.inProgress +
      treatment.accepted +
      treatment.acceptedUndefined >
    0;

  return (
    <Container display={"flex"} gap={0.25} mx={0.25} my={0.25} wrap={"wrap"}>
      {isThereTreatments ? (
        <Fragment>
          <TreatmentTag $variant={"untreated"}>
            <i className={"fa-solid fa-circle fa-2xs"} />
            {`Untreated = ${treatment.untreated}`}
          </TreatmentTag>
          <TreatmentTag $variant={"in_progress"}>
            <i className={"fa-solid fa-circle fa-2xs"} />
            {`In progress = ${treatment.inProgress}`}
          </TreatmentTag>
          <TreatmentTag $variant={"accepted"}>
            <i className={"fa-solid fa-circle fa-2xs"} />
            {`Temporarily acceptance = ${treatment.accepted}`}
          </TreatmentTag>
          <TreatmentTag $variant={"accepted_undefined"}>
            <i className={"fa-solid fa-circle fa-2xs"} />
            {`Permanently acceptance = ${treatment.acceptedUndefined}`}
          </TreatmentTag>
        </Fragment>
      ) : (
        <Text fontStyle={"italic"} size={"sm"}>
          {"Not applied"}
        </Text>
      )}
    </Container>
  );
};

export {
  FindingPriorityFormatter,
  FindingTitleFormatter,
  SeverityOverviewFormatter,
  FindingTreatmentFormatter,
};
